import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserPlus, Crown, Clock, TrendingUp, Trash2, Mail, Phone, MapPin, Calendar, MessageSquare, Gift, Tag, X, Check, MoreVertical, Ban, Pause, Play, AlertTriangle, ShieldOff, User, Pencil, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import { API_URL } from '../utils/api';

const AdminUsersPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Promo code modal state
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  
  // Membership action modal state
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [membershipAction, setMembershipAction] = useState('');
  const [membershipReason, setMembershipReason] = useState('');
  const [processingMembership, setProcessingMembership] = useState(false);
  
  // Edit profile modal state
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [editProfileData, setEditProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    location: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Check if admin
  const isAdmin = user?.email === 'ebonnie_faith@hotmail.com' || user?.email === 'craftersmarketaus@gmail.com' || user?.is_admin;

  useEffect(() => {
    if (isAdmin && token) {
      fetchUsers();
      fetchStats();
      fetchPromoCodes();
    }
  }, [isAdmin, token]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (err) {
      console.error('Failed to fetch users', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(res.data.codes || []);
    } catch (err) {
      console.error('Failed to fetch promo codes', err);
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      const res = await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || `User ${userName} deleted and email blacklisted`);
      fetchUsers();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete user', err);
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openMembershipModal = (targetUser, action) => {
    setSelectedUser(targetUser);
    setMembershipAction(action);
    setMembershipReason('');
    setMembershipModalOpen(true);
  };

  const handleMembershipAction = async () => {
    if (!selectedUser || !membershipAction) return;
    
    setProcessingMembership(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/membership`,
        { action: membershipAction, reason: membershipReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setMembershipModalOpen(false);
      fetchUsers();
      fetchStats();
    } catch (err) {
      console.error('Failed to perform membership action', err);
      toast.error(err.response?.data?.detail || 'Failed to perform action');
    } finally {
      setProcessingMembership(false);
    }
  };

  const openPromoModal = (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedCode('');
    setPromoModalOpen(true);
  };

  const applyPromoCode = async () => {
    if (!selectedUser || !selectedCode) return;

    setApplyingCode(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/apply-promo`,
        { user_id: selectedUser.id, code: selectedCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setPromoModalOpen(false);
      fetchUsers();
      fetchStats();
    } catch (err) {
      console.error('Failed to apply promo code', err);
      toast.error(err.response?.data?.detail || 'Failed to apply promo code');
    } finally {
      setApplyingCode(false);
    }
  };

  const handleMessageUser = (targetUser) => {
    // Navigate to messages page with the user's ID to start/open conversation
    navigate(`/messages?user=${targetUser.id}`);
  };

  const openEditProfileModal = (targetUser) => {
    setEditingProfile(targetUser);
    setEditProfileData({
      name: targetUser.name || '',
      email: targetUser.email || '',
      phone: targetUser.phone || '',
      bio: targetUser.bio || '',
      location: targetUser.location || ''
    });
    setEditProfileModalOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!editingProfile) return;
    
    setSavingProfile(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${editingProfile.id}/profile`,
        editProfileData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Profile updated for ${editProfileData.name}`);
      setEditProfileModalOpen(false);
      fetchUsers();
    } catch (err) {
      console.error('Failed to update profile', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const viewUserProfile = (targetUser) => {
    // Open profile in new tab
    window.open(`/profile/${targetUser.id}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, isFrozen, isSuspended) => {
    if (isSuspended) return 'bg-red-100 text-red-700';
    if (isFrozen) return 'bg-orange-100 text-orange-700';
    const styles = {
      active: 'bg-green-100 text-green-700',
      trial: 'bg-blue-100 text-blue-700',
      free: 'bg-stone-100 text-stone-600',
      free_for_life: 'bg-purple-100 text-purple-700',
      cancelled: 'bg-teal-100 text-teal-600',
      past_due: 'bg-orange-100 text-orange-700'
    };
    return styles[status] || styles.free;
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">Access Denied</h1>
          <p className="text-stone-500 mt-2">You don&apos;t have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  // Get active promo codes (not expired)
  const activePromoCodes = promoCodes.filter(c => !c.is_expired);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          User Management
        </h1>
        <p className="text-stone-500 mb-8">View and manage all registered users</p>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-stone-500 mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Total Users</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.total_users}</p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-stone-500 mb-1">
                <Crown className="h-4 w-4" />
                <span className="text-xs">Sellers</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.sellers}</p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-blue-500 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs">On Trial</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{stats.trial_users}</p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-green-500 mb-1">
                <Crown className="h-4 w-4" />
                <span className="text-xs">Paid</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.active_subscribers}</p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-stone-500 mb-1">
                <UserPlus className="h-4 w-4" />
                <span className="text-xs">This Week</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.new_users_week}</p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center gap-2 text-stone-500 mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">This Month</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.new_users_month}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Users Table */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Joined</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-stone-500">
                      <div className="animate-spin h-6 w-6 border-2 border-stone-300 border-t-teal-600 rounded-full mx-auto" />
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-stone-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-stone-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-teal-600 font-semibold text-sm">
                              {u.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            {/* Clickable user name - navigates to messages */}
                            <button
                              onClick={() => handleMessageUser(u)}
                              className="font-medium text-stone-900 hover:text-teal-600 hover:underline text-left flex items-center gap-1"
                              title="Click to message this user"
                            >
                              {u.name}
                              <MessageSquare className="h-3 w-3 text-stone-400" />
                            </button>
                            {u.is_admin && (
                              <span className="ml-2 text-xs bg-teal-100 text-teal-600 px-2 py-0.5 rounded-full">
                                Admin
                              </span>
                            )}
                            {u.location && (
                              <p className="text-xs text-stone-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {u.location}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <p className="text-sm text-stone-600 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {u.email}
                          </p>
                          {u.phone && (
                            <p className="text-sm text-stone-500 flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {u.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          {/* Suspended indicator */}
                          {u.account_suspended && (
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium flex items-center gap-1">
                                <ShieldOff className="h-3 w-3" />
                                SUSPENDED
                              </span>
                            </div>
                          )}
                          
                          {/* Frozen indicator */}
                          {u.account_frozen && !u.account_suspended && (
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium flex items-center gap-1">
                                <Pause className="h-3 w-3" />
                                FROZEN
                              </span>
                            </div>
                          )}
                          
                          {/* Seller Status */}
                          <div className="flex items-center gap-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(u.subscription_status, u.account_frozen, u.account_suspended)}`}>
                              {u.account_suspended ? 'Suspended' : u.account_frozen ? 'Frozen' : u.subscription_status === 'free_for_life' ? 'VIP' : (u.subscription_status || 'free')}
                            </span>
                            {u.is_seller && (
                              <span className="text-xs text-stone-500">Seller</span>
                            )}
                          </div>
                          
                          {/* Tradie Status */}
                          {u.is_tradie && (
                            <div className="flex items-center gap-1">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                u.tradie_subscription_status === 'free_for_life' ? 'bg-purple-100 text-purple-700' :
                                u.tradie_subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                                u.tradie_subscription_status === 'trial' ? 'bg-blue-100 text-blue-700' :
                                'bg-stone-100 text-stone-600'
                              }`}>
                                {u.tradie_subscription_status === 'free_for_life' ? 'VIP' : 
                                 (u.tradie_subscription_status || 'pending')}
                              </span>
                              <span className="text-xs text-stone-500">Tradie</span>
                            </div>
                          )}
                          
                          {/* Promo Code */}
                          {u.promo_code_used ? (
                            <p className={`text-xs flex items-center gap-1 ${
                              u.promo_code_used === 'CMFFL26EF' ? 'text-purple-600 font-medium' : 'text-green-600'
                            }`}>
                              <Tag className="h-3 w-3" />
                              {u.promo_code_used}
                              {u.promo_code_used === 'CMFFL26EF' && ' (VIP)'}
                            </p>
                          ) : (u.subscription_status === 'trial' || u.is_seller) && (
                            <p className="text-xs text-orange-500">No code applied</p>
                          )}
                          
                          {/* Tradie promo code */}
                          {u.tradie_promo_code && (
                            <p className="text-xs flex items-center gap-1 text-blue-600">
                              <Tag className="h-3 w-3" />
                              {u.tradie_promo_code} (Tradie)
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-stone-600">{formatDate(u.created_at)}</p>
                        {/* Seller trial end */}
                        {u.trial_ends_at && u.subscription_status === 'trial' && (
                          <p className="text-xs text-blue-600">
                            Seller trial: {formatDate(u.trial_ends_at)}
                          </p>
                        )}
                        {/* Tradie trial end */}
                        {u.tradie_trial_ends_at && u.tradie_subscription_status === 'trial' && (
                          <p className="text-xs text-blue-600">
                            Tradie trial: {formatDate(u.tradie_trial_ends_at)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Apply Code Button - show for trial/free users without lifetime code */}
                          {!u.is_admin && u.promo_code_used !== 'CMFFL26EF' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPromoModal(u)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Apply promo code"
                            >
                              <Gift className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {/* Message Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMessageUser(u)}
                            className="text-stone-600 hover:text-stone-700 hover:bg-stone-50"
                            title="Message user"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          
                          {/* Membership Management Dropdown - only for sellers/tradies */}
                          {!u.is_admin && (u.is_seller || u.is_tradie) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-stone-600 hover:text-stone-700"
                                  title="Manage membership"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {/* View & Edit Profile */}
                                <DropdownMenuItem 
                                  onClick={() => viewUserProfile(u)}
                                >
                                  <User className="h-4 w-4 mr-2" />
                                  View Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => openEditProfileModal(u)}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Profile
                                </DropdownMenuItem>
                                
                                <DropdownMenuSeparator />
                                
                                {/* Suspend/Unsuspend Account - Most severe action */}
                                {u.account_suspended ? (
                                  <DropdownMenuItem 
                                    onClick={() => openMembershipModal(u, 'unsuspend')}
                                    className="text-green-600 focus:text-green-600"
                                  >
                                    <ShieldOff className="h-4 w-4 mr-2" />
                                    Unsuspend Account
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem 
                                    onClick={() => openMembershipModal(u, 'suspend')}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <ShieldOff className="h-4 w-4 mr-2" />
                                    Suspend Account
                                  </DropdownMenuItem>
                                )}
                                
                                <DropdownMenuSeparator />
                                
                                {/* Cancel Membership */}
                                <DropdownMenuItem 
                                  onClick={() => openMembershipModal(u, 'cancel')}
                                  className="text-teal-600 focus:text-teal-600"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Cancel Membership
                                </DropdownMenuItem>
                                
                                {/* Freeze/Unfreeze */}
                                {u.account_frozen ? (
                                  <DropdownMenuItem 
                                    onClick={() => openMembershipModal(u, 'unfreeze')}
                                    className="text-green-600 focus:text-green-600"
                                  >
                                    <Play className="h-4 w-4 mr-2" />
                                    Unfreeze Account
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem 
                                    onClick={() => openMembershipModal(u, 'freeze')}
                                    className="text-orange-600 focus:text-orange-600"
                                  >
                                    <Pause className="h-4 w-4 mr-2" />
                                    Freeze Membership
                                  </DropdownMenuItem>
                                )}
                                
                                <DropdownMenuSeparator />
                                
                                {/* Delete Member */}
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setMembershipAction('delete');
                                    setMembershipModalOpen(true);
                                  }}
                                  className="text-teal-600 focus:text-teal-600 focus:bg-teal-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Member
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          
                          {/* Simple Delete Button for non-seller/tradie users */}
                          {!u.is_admin && !u.is_seller && !u.is_tradie && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-teal-600 hover:text-teal-600 hover:bg-teal-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete User</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete {u.name}? Their email will be blacklisted - if they sign up again, they won&apos;t get a free trial.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteUser(u.id, u.name)}
                                    className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Apply Promo Code Modal */}
      <Dialog open={promoModalOpen} onOpenChange={setPromoModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-blue-600" />
              Apply Promo Code
            </DialogTitle>
            <DialogDescription>
              Apply a promo code on behalf of {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Info */}
            <div className="bg-stone-50 rounded-lg p-3">
              <p className="font-medium text-stone-900">{selectedUser?.name}</p>
              <p className="text-sm text-stone-500">{selectedUser?.email}</p>
              {selectedUser?.promo_code_used && (
                <p className="text-sm text-orange-600 mt-1">
                  Current code: {selectedUser.promo_code_used}
                </p>
              )}
              {selectedUser?.trial_ends_at && (
                <p className="text-sm text-blue-600 mt-1">
                  Trial ends: {formatDateTime(selectedUser.trial_ends_at)}
                </p>
              )}
            </div>
            
            {/* Code Selection */}
            <div className="space-y-2">
              <Label htmlFor="promo-code">Select Promo Code</Label>
              <Select value={selectedCode} onValueChange={setSelectedCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a code..." />
                </SelectTrigger>
                <SelectContent>
                  {activePromoCodes.map((code) => (
                    <SelectItem key={code.code} value={code.code}>
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-mono font-medium">{code.code}</span>
                        <span className="text-sm text-stone-500">
                          {code.trial_days === 36500 ? 'Free for life' : 
                           code.applies_to === 'tradie' ? `${code.trial_days} days (Tradie)` :
                           `${code.trial_days} days`}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedCode && (
                <div className={`rounded-lg p-3 mt-2 ${
                  promoCodes.find(c => c.code === selectedCode)?.free_for_life 
                    ? 'bg-purple-50 border border-purple-200' 
                    : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`text-sm ${
                    promoCodes.find(c => c.code === selectedCode)?.free_for_life 
                      ? 'text-purple-800' 
                      : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                      ? 'text-green-800'
                      : 'text-blue-800'
                  }`}>
                    <strong>{selectedCode}</strong>: {
                      promoCodes.find(c => c.code === selectedCode)?.free_for_life 
                        ? '🌟 FREE FOR LIFE - All Services!' 
                        : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                        ? `${promoCodes.find(c => c.code === selectedCode)?.trial_days} days tradie trial + ${promoCodes.find(c => c.code === selectedCode)?.discount_percent || 20}% off first month`
                        : `${promoCodes.find(c => c.code === selectedCode)?.trial_days} days free`
                    }
                  </p>
                  {promoCodes.find(c => c.code === selectedCode)?.description && (
                    <p className={`text-xs mt-1 ${
                      promoCodes.find(c => c.code === selectedCode)?.free_for_life 
                        ? 'text-purple-600' 
                        : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                        ? 'text-green-600'
                        : 'text-blue-600'
                    }`}>
                      {promoCodes.find(c => c.code === selectedCode)?.description}
                    </p>
                  )}
                  {/* Show what the code applies to */}
                  <p className={`text-xs mt-2 ${
                    promoCodes.find(c => c.code === selectedCode)?.free_for_life 
                      ? 'text-purple-700 font-medium' 
                      : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                      ? 'text-green-700 font-medium'
                      : 'text-blue-700'
                  }`}>
                    Applies to: {
                      promoCodes.find(c => c.code === selectedCode)?.applies_to === 'all'
                        ? 'Selling + Tradie Advertising (both free!)'
                        : promoCodes.find(c => c.code === selectedCode)?.applies_to === 'tradie'
                        ? 'Tradie subscription only'
                        : 'Selling subscription only'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPromoModalOpen(false)}
              disabled={applyingCode}
            >
              Cancel
            </Button>
            <Button
              onClick={applyPromoCode}
              disabled={!selectedCode || applyingCode}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {applyingCode ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Applying...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Apply Code
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Membership Action Modal */}
      <Dialog open={membershipModalOpen} onOpenChange={setMembershipModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {membershipAction === 'cancel' && <Ban className="h-5 w-5 text-teal-600" />}
              {membershipAction === 'freeze' && <Pause className="h-5 w-5 text-orange-600" />}
              {membershipAction === 'unfreeze' && <Play className="h-5 w-5 text-green-600" />}
              {membershipAction === 'suspend' && <ShieldOff className="h-5 w-5 text-red-600" />}
              {membershipAction === 'unsuspend' && <ShieldOff className="h-5 w-5 text-green-600" />}
              {membershipAction === 'delete' && <Trash2 className="h-5 w-5 text-teal-600" />}
              {membershipAction === 'cancel' && 'Cancel Membership'}
              {membershipAction === 'freeze' && 'Freeze Account'}
              {membershipAction === 'unfreeze' && 'Unfreeze Account'}
              {membershipAction === 'suspend' && 'Suspend Account'}
              {membershipAction === 'unsuspend' && 'Unsuspend Account'}
              {membershipAction === 'delete' && 'Delete Member'}
            </DialogTitle>
            <DialogDescription>
              {membershipAction === 'cancel' && `Cancel ${selectedUser?.name}'s membership. They will lose seller/tradie status but keep their account.`}
              {membershipAction === 'freeze' && `Freeze ${selectedUser?.name}'s account. All their listings and posts will be hidden temporarily.`}
              {membershipAction === 'unfreeze' && `Unfreeze ${selectedUser?.name}'s account. Their listings and posts will be restored.`}
              {membershipAction === 'suspend' && `Suspend ${selectedUser?.name}'s account for violating Terms of Service. They will be blocked from logging in and all content will be hidden.`}
              {membershipAction === 'unsuspend' && `Restore ${selectedUser?.name}'s account access. They will be able to log in again.`}
              {membershipAction === 'delete' && `Permanently delete ${selectedUser?.name}'s account and all their data.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Info */}
            <div className="bg-stone-50 rounded-lg p-3">
              <p className="font-medium text-stone-900">{selectedUser?.name}</p>
              <p className="text-sm text-stone-500">{selectedUser?.email}</p>
              {selectedUser?.is_seller && (
                <span className="inline-block mt-1 mr-1 text-xs bg-stone-200 text-stone-700 px-2 py-0.5 rounded">Seller</span>
              )}
              {selectedUser?.is_tradie && (
                <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Tradie</span>
              )}
            </div>
            
            {/* Warning for delete */}
            {membershipAction === 'delete' && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-teal-700">This action cannot be undone!</p>
                    <p className="text-xs text-teal-600 mt-1">
                      All listings, posts, and messages will be permanently deleted. 
                      Their email will be blacklisted - if they sign up again, they won&apos;t get a free trial.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Warning for suspend */}
            {membershipAction === 'suspend' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ShieldOff className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Account will be blocked!</p>
                    <p className="text-xs text-red-600 mt-1">
                      The user will NOT be able to log in. All their listings, posts, and content will be hidden from public view.
                      You can unsuspend them later if needed.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Reason input for cancel/freeze/suspend */}
            {(membershipAction === 'cancel' || membershipAction === 'freeze' || membershipAction === 'suspend') && (
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Enter reason for this action..."
                  value={membershipReason}
                  onChange={(e) => setMembershipReason(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMembershipModalOpen(false)}
              disabled={processingMembership}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (membershipAction === 'delete') {
                  deleteUser(selectedUser.id, selectedUser.name);
                  setMembershipModalOpen(false);
                } else {
                  handleMembershipAction();
                }
              }}
              disabled={processingMembership}
              className={
                membershipAction === 'unfreeze' || membershipAction === 'unsuspend'
                  ? 'bg-green-600 hover:bg-green-700' 
                  : membershipAction === 'freeze'
                  ? 'bg-orange-600 hover:bg-orange-700'
                  : membershipAction === 'suspend'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-teal-600 hover:bg-gradient-to-r from-sky-500 to-teal-500'
              }
            >
              {processingMembership ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  {membershipAction === 'cancel' && 'Cancel Membership'}
                  {membershipAction === 'freeze' && 'Freeze Account'}
                  {membershipAction === 'unfreeze' && 'Unfreeze Account'}
                  {membershipAction === 'suspend' && 'Suspend Account'}
                  {membershipAction === 'unsuspend' && 'Unsuspend Account'}
                  {membershipAction === 'delete' && 'Delete Permanently'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={editProfileModalOpen} onOpenChange={setEditProfileModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-teal-600" />
              Edit User Profile
            </DialogTitle>
            <DialogDescription>
              Update profile information for {editingProfile?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editProfileData.name}
                onChange={(e) => setEditProfileData({ ...editProfileData, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editProfileData.email}
                onChange={(e) => setEditProfileData({ ...editProfileData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            
            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editProfileData.phone}
                onChange={(e) => setEditProfileData({ ...editProfileData, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            
            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={editProfileData.location}
                onChange={(e) => setEditProfileData({ ...editProfileData, location: e.target.value })}
                placeholder="City, State"
              />
            </div>
            
            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="edit-bio">Bio / About</Label>
              <Textarea
                id="edit-bio"
                value={editProfileData.bio}
                onChange={(e) => setEditProfileData({ ...editProfileData, bio: e.target.value })}
                placeholder="User's bio or about section..."
                rows={4}
              />
              <p className="text-xs text-stone-500">
                This appears on their public profile page
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditProfileModalOpen(false)}
              disabled={savingProfile}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              {savingProfile ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
